import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { heroSlides, heroStats } from '../../data/mock';

export const HeroSlider = () => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);

  const goToSlide = useCallback((index) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  }, [current]);

  const next = useCallback(() => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % heroSlides.length);
  }, []);

  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  // Auto-play
  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = heroSlides[current];

  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
  };

  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[rgb(17,17,19)]">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[rgba(243,112,33,0.06)] rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 -right-32 w-80 h-80 bg-[rgba(0,180,216,0.08)] rounded-full blur-[100px]" />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
      </div>

      <div className="relative max-w-[1200px] mx-auto px-6 py-20 w-full">
        {/* Slide Content */}
        <div className="max-w-3xl">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={slide.id}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.5, ease: 'easeInOut' }}
            >
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(243,112,33,0.1)] border border-[rgba(243,112,33,0.2)] mb-8">
                <span className="w-2 h-2 rounded-full bg-[#F37021] animate-pulse" />
                <span className="text-[#F37021] text-sm font-medium">{slide.badge}</span>
              </div>

              {/* Headline */}
              <h1 className="text-5xl md:text-6xl lg:text-[68px] font-bold text-white leading-[1.05] tracking-tight mb-6">
                {slide.headline}{' '}
                <span className="text-[#F37021]">{slide.highlightedText}</span>
                {slide.subHeadline && <> {slide.subHeadline}</>}
              </h1>

              {/* Description */}
              <p className="text-lg md:text-xl text-[rgb(218,218,218)] leading-relaxed mb-10 max-w-xl">
                {slide.description}
              </p>

              {/* CTAs */}
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button className="bg-[#F37021] hover:bg-[#DC5A14] text-white font-semibold rounded-xl px-8 h-14 text-base border-0 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(243,112,33,0.35)]">
                    Enquire Now <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/courses">
                  <Button
                    variant="outline"
                    className="border-2 border-[rgb(63,63,63)] text-white hover:border-[#F37021] hover:text-[#F37021] hover:bg-[rgba(243,112,33,0.06)] rounded-xl px-8 h-14 text-base bg-transparent transition-all duration-200 hover:-translate-y-0.5"
                  >
                    Explore Courses
                  </Button>
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Slider Controls */}
        <div className="flex items-center gap-4 mt-14">
          <button
            onClick={prev}
            className="w-11 h-11 rounded-xl bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] flex items-center justify-center text-white hover:border-[#F37021] hover:text-[#F37021] transition-all duration-200"
            aria-label="Previous slide"
          >
            <ChevronLeft size={20} />
          </button>

          {/* Dots */}
          <div className="flex gap-2">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === current
                    ? 'w-8 bg-[#F37021]'
                    : 'w-2 bg-[rgb(63,63,63)] hover:bg-[rgb(100,100,100)]'
                }`}
              />
            ))}
          </div>

          <button
            onClick={next}
            className="w-11 h-11 rounded-xl bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] flex items-center justify-center text-white hover:border-[#F37021] hover:text-[#F37021] transition-all duration-200"
            aria-label="Next slide"
          >
            <ChevronRight size={20} />
          </button>

          <span className="text-[rgb(161,161,170)] text-sm ml-2 font-medium">
            {String(current + 1).padStart(2, '0')}
            <span className="text-[rgb(63,63,63)] mx-1">/</span>
            {String(heroSlides.length).padStart(2, '0')}
          </span>
        </div>

        {/* Stats Bar */}
   <div className="mt-16 pt-12 border-t border-white/10">
  <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
    {heroStats.map((stat, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 + i * 0.12 }}
        className="space-y-1"
      >
        <div className="text-3xl md:text-4xl font-semibold tracking-tight text-white">
          {stat.value}
        </div>

        <div className="text-sm font-medium text-[rgb(161,161,170)]">
          {stat.label}
        </div>

        {stat.subtext && (
          <div className="text-xs text-white/50 leading-snug max-w-[180px]">
            {stat.subtext}
          </div>
        )}
      </motion.div>
    ))}
  </div>
</div>

      </div>
    </section>
  );
};
